import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { FinetuneRunNew } from "../../../model_gen/finetuning";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import { notification, Tag } from "antd";
import {
  FlaskConicalIcon,
  PlusIcon,
  PlayIcon,
  Loader2Icon,
  XIcon,
} from "lucide-react";

interface LatestRun {
  id: number;
  status: string;
  date_created?: number;
  accuracy: number | null;
  evaluated_requests: number;
}

interface AssignedTestSet {
  test_set_id: number;
  test_set_name: string;
  is_golden: boolean;
  request_count: number | null;
  intent_group_id: number | null;
  latest_run: LatestRun | null;
}

interface AvailableTestSet {
  id: number;
  name: string;
  is_golden: boolean;
  request_count: number | null;
}

interface BenchmarkSummary {
  assigned_test_sets: AssignedTestSet[];
  available_golden_test_sets: AvailableTestSet[];
  overall_accuracy: number | null;
  overall_evaluated_requests: number;
}

interface FinetuneRunEvaluationsProps {
  finetuneRun: FinetuneRunNew;
  apiKey: ApiKeyOrGetter;
  onFinetuneRunUpdate: (run: FinetuneRunNew) => void;
}

const FinetuneRunEvaluations: React.FC<FinetuneRunEvaluationsProps> = ({
  finetuneRun,
  apiKey,
  onFinetuneRunUpdate,
}) => {
  const [summary, setSummary] = useState<BenchmarkSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTriggering, setIsTriggering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTestSetPicker, setShowTestSetPicker] = useState(false);

  const assignedIds = finetuneRun.meta?.auto_test_set_ids || [];
  const autoTestStatus = finetuneRun.meta?.auto_test_status;
  const isPipelineActive =
    autoTestStatus && !["COMPLETED", "FAILED"].includes(autoTestStatus);
  const isCompleted = finetuneRun.status === "COMPLETED";

  const fetchSummary = useCallback(() => {
    if (!apiKey || !finetuneRun.id) return;
    setIsLoading(true);
    FinetuneRunService.getBenchmarkSummary(
      finetuneRun.id,
      undefined,
      apiKey
    ).subscribe(
      (data: BenchmarkSummary) => {
        setSummary(data);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  }, [apiKey, finetuneRun.id, assignedIds.join(",")]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!isPipelineActive || !apiKey) return;

    const interval = setInterval(() => {
      FinetuneRunService.getFinetuneRunById(
        finetuneRun.id!,
        undefined,
        apiKey
      ).subscribe(
        (data: FinetuneRunNew) => {
          onFinetuneRunUpdate(data);
          if (
            data.meta?.auto_test_status &&
            ["COMPLETED", "FAILED"].includes(data.meta.auto_test_status)
          ) {
            fetchSummary();
          }
        },
        () => {}
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isPipelineActive, apiKey, finetuneRun.id]);

  const handleToggleTestSet = (testSetId: number) => {
    const newIds = assignedIds.includes(testSetId)
      ? assignedIds.filter((id) => id !== testSetId)
      : [...assignedIds, testSetId];

    setIsSaving(true);
    FinetuneRunService.updateAutoTestSets(
      finetuneRun.id!,
      newIds,
      undefined,
      apiKey
    ).subscribe(
      (updated: FinetuneRunNew) => {
        onFinetuneRunUpdate(updated);
        setIsSaving(false);
        fetchSummary();
      },
      (err: any) => {
        console.error("Failed to update test sets:", err);
        notification.error({
          message: "Error",
          description: "Failed to update test sets",
        });
        setIsSaving(false);
      }
    );
  };

  const handleTriggerTests = () => {
    if (assignedIds.length === 0) {
      notification.warning({
        message: "No Test Sets",
        description: "Please assign at least one test set before running.",
      });
      return;
    }

    setIsTriggering(true);
    FinetuneRunService.triggerAutoTest(
      finetuneRun.id!,
      assignedIds,
      undefined,
      apiKey
    ).subscribe(
      (updated: FinetuneRunNew) => {
        onFinetuneRunUpdate(updated);
        setIsTriggering(false);
        notification.success({
          message: "Tests Triggered",
          description: "The benchmark pipeline has been started.",
        });
      },
      (err: any) => {
        console.error("Failed to trigger tests:", err);
        notification.error({
          message: "Error",
          description: "Failed to trigger benchmark pipeline",
        });
        setIsTriggering(false);
      }
    );
  };

  const assigned = summary?.assigned_test_sets ?? [];
  const goldenPicker = summary?.available_golden_test_sets ?? [];

  return (
    <div className="space-y-6">
      {/* Overall Summary */}
      {summary?.overall_accuracy !== null &&
        summary?.overall_accuracy !== undefined && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-border">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                  Overall Accuracy
                </p>
                <p className="text-3xl font-gc-demi-bold text-foreground">
                  {summary.overall_accuracy.toFixed(1)}%
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                  Total Requests Evaluated
                </p>
                <p className="text-3xl font-gc-demi-bold text-foreground">
                  {summary.overall_evaluated_requests.toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

      {/* Pipeline Status Banner */}
      {autoTestStatus && (
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FlaskConicalIcon className="w-5 h-5 text-maitai-green" />
              <span className="text-sm font-gc-med text-foreground">
                Pipeline Status:
              </span>
              {(() => {
                const colorMap: Record<string, string> = {
                  DEPLOYING: "blue",
                  TESTING: "processing",
                  TEARING_DOWN: "orange",
                  COMPLETED: "success",
                  FAILED: "error",
                };
                return (
                  <Tag color={colorMap[autoTestStatus] || "default"}>
                    {autoTestStatus}
                    {isPipelineActive && (
                      <Loader2Icon className="w-3 h-3 ml-1 inline animate-spin" />
                    )}
                  </Tag>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Sets Card */}
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-gc-med text-foreground">Test Sets</h3>
            <div className="flex items-center gap-2">
              {isCompleted && assignedIds.length > 0 && (
                <Button
                  onClick={handleTriggerTests}
                  disabled={isTriggering || !!isPipelineActive}
                  size="sm"
                  className="gap-1.5">
                  {isTriggering ? (
                    <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <PlayIcon className="w-3.5 h-3.5" />
                  )}
                  Run Tests
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setShowTestSetPicker(!showTestSetPicker)}>
                <PlusIcon className="w-3.5 h-3.5" />
                {showTestSetPicker ? "Done" : "Add Tests"}
              </Button>
            </div>
          </div>

          {/* Test Set Picker */}
          {showTestSetPicker && (
            <div className="mb-4 p-4 rounded-lg border border-border bg-muted/50">
              <p className="text-sm text-muted-foreground mb-3 font-gc-reg">
                Select golden test sets to assign:
              </p>
              {isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                  Loading test sets...
                </div>
              ) : goldenPicker.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No golden test sets found. Create one first.
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {goldenPicker.map((ts) => (
                    <label
                      key={ts.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-accent cursor-pointer transition-colors">
                      <Checkbox
                        checked={assignedIds.includes(ts.id)}
                        onCheckedChange={() => handleToggleTestSet(ts.id)}
                        disabled={isSaving}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-gc-med text-foreground truncate block">
                          {ts.name}
                        </span>
                        {ts.request_count != null && (
                          <span className="text-xs text-muted-foreground">
                            {ts.request_count} requests
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Test Sets Table */}
          {assigned.length === 0 && !isLoading ? (
            <p className="text-sm text-muted-foreground font-gc-reg">
              No test sets assigned. Click "Add Tests" to assign golden test
              sets.
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2 pr-4">
                    Test Set
                  </th>
                  <th className="text-right text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2 pr-4">
                    Requests
                  </th>
                  <th className="text-right text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2 pr-4">
                    Accuracy
                  </th>
                  <th className="text-right text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {assigned.map((entry) => {
                  const run = entry.latest_run;
                  const isRunning =
                    run &&
                    ["RUNNING", "CREATED"].includes(
                      run.status?.toUpperCase()
                    );

                  return (
                    <tr
                      key={entry.test_set_id}
                      className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                      <td className="py-3 pr-4">
                        {run ? (
                          <Link
                            to={`/intent-group/${entry.intent_group_id}/testsets/${entry.test_set_id}/runs/${run.id}`}
                            className="text-sm font-gc-med text-maitai-green hover:underline">
                            {entry.test_set_name}
                          </Link>
                        ) : (
                          <span className="text-sm font-gc-med text-foreground">
                            {entry.test_set_name}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <span className="text-sm text-foreground font-gc-reg">
                          {entry.request_count != null
                            ? entry.request_count.toLocaleString()
                            : "—"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {run?.status?.toUpperCase() === "COMPLETED" &&
                        run.accuracy !== null ? (
                          <span className="text-sm font-gc-demi-bold text-foreground">
                            {run.accuracy.toFixed(1)}%
                          </span>
                        ) : run?.status?.toUpperCase() === "FAILED" ? (
                          <span className="text-sm text-red-600 font-gc-med">
                            Failed
                          </span>
                        ) : isRunning ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <Loader2Icon className="w-3.5 h-3.5 animate-spin text-blue-600" />
                            <span className="text-sm text-blue-600 font-gc-med">
                              Running
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            —
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() =>
                              handleToggleTestSet(entry.test_set_id)
                            }
                            disabled={isSaving}
                            className="text-muted-foreground hover:text-red-600 transition-colors">
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinetuneRunEvaluations;
